/**
 * Where a partner switch should land, given the route the admin is currently on.
 *
 * Switching partner always does a full page load — that is the only clean way to
 * re-read the partner cookie across nav, feature flags and every partner-scoped
 * query. The open question is which URL to load, and it comes down to whether the
 * current one names a record belonging to the partner being left behind:
 *
 * - `home` — the route has params, so the path itself carries an id (e.g.
 *   `/projects/$projectId`). Nothing to salvage; go to the app home.
 * - `stripSearch` — the route opted in via `resetSearchOnPartnerChange` because
 *   its *search* params name a record. The Inbox's `?selected=` is a job card, and
 *   carrying it over asks the new partner's API for the old partner's card, which
 *   404s and strands the detail pane. Reload the path without the query string.
 * - `reload` — nothing partner-scoped in the URL; reload it as-is so filters and
 *   other view state survive the switch.
 */
export type PartnerSwitchDestination = 'home' | 'stripSearch' | 'reload';

interface PartnerSwitchMatch {
  params?: Record<string, unknown>;
  staticData?: { resetSearchOnPartnerChange?: boolean };
}

export function partnerSwitchDestination(
  leaf: PartnerSwitchMatch | undefined,
): PartnerSwitchDestination {
  if (!leaf) return 'reload';
  if (Object.keys(leaf.params ?? {}).length > 0) return 'home';
  if (leaf.staticData?.resetSearchOnPartnerChange) return 'stripSearch';
  return 'reload';
}
